<template>
    <div class="line-chart">
      <Tooltip
        :show="showTooltip"
        v-bind="tooltipContent"
        :position="tooltipPosition"
        :chartWidth="chartWidth"
      />
      <ChartWrapper
        :data="timeParsedData"
        :width="chartWidth"
        :height="chartHeight"
        :chartDefaults="chartDefaults"
        :primaryKey="dataKey.snake"
        :dimensionKey="dimensionKey"
      >
        <template #x-axis="props">
          <XAxis
            v-bind="props"
            :scale="'timeSeries'"
            :textColor="chartColor"
            :lineColor="chartColor"
          />
        </template>
        <template #y-axis="props">
          <YAxis
            v-bind="props"
            :scale="'linear'"
            :isInPercentages="isInPercentages"
            intervalStyle="stroke-dasharray: 4 2; stroke: #C1CCD4"
            :textColor="chartColor"
          />
        </template>
        <template #line-chart="props">
          <LineChart
            v-bind="props"
            :hoveredIndex="hoveredIndex"
            :scale="'timeSeries'"
            :height="chartLineHeight"
            :showVerticals="false"
            @onHover="setTooltip"
            @onLeave="hideTooltip"
          />
        </template>
      </ChartWrapper>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  getReportFieldKeyFromSnake,
} from '../../../../services/dashboard/reportFieldService';
import {
  getDisplayValue,
  getValueAndLabel,
} from '../../../../services/dashboard/displayFormatService.js';
import ChartWrapper from '../../Common/Charting/Shared/Graph/ChartWrapper.vue';
import XAxis from '../../Common/Charting/Shared/Graph/Axes/XAxis.vue';
import YAxis from '../../Common/Charting/Shared/Graph/Axes/YAxis.vue';
import LineChart from '../../Common/Charting/Shared/Graph/LineChart/LineChart.vue';
import Tooltip from '../../Common/Tooltips/ChartTooltip/Tooltip.vue';
import { timeParse, timeFormat } from 'd3-time-format';

export default {
  props: {
    data: {
      type: Array,
      required: true,
    },
    dataKey: {
      type: Object,
      required: true,
    },
    chartWidth: {
      type: Number,
    },
    chartHeight: {
      type: Number,
    },
    isInPercentages: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ChartWrapper,
    XAxis,
    YAxis,
    LineChart,
    Tooltip,
  },
  data() {
    return {
      tooltipContent: {
        title: '',
        subtitle: '',
        content: [],
      },
      tooltipPosition: {},
      chartDefaults: {
        dataWidth: 0,
        xLabelPadding: 16,
        yLabelPadding: 8,
        yLabelWidth: 30,
        fontSize: 12,
        margin: {
          top: 24,
          right: 10,
          bottom: 0,
          left: 10,
        },
      },
      hoveredIndex: null,
      showTooltip: false,
      dimensionKey: 'event_date',
      chartColor: '#778699',
    };
  },
  computed: {
    ...mapState({
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
    }),
    summedChartData() {
      const summedByDate = {};
      const summedData = [];

      for (let i = 0; i < this.data.length; i++) {
        const currentEl = this.data[i];
        const currentDate = currentEl.event_date.value;
        let currentVal = currentEl[this.dataKey.snake];

        // `null`s will break the line chart
        if (currentVal === null) currentVal = 0;

        if (summedByDate[currentDate]) {
          summedByDate[currentDate] = summedByDate[currentDate] + currentVal;
        } else {
          summedByDate[currentDate] = currentVal;
        }
      }

      for (const [key, value] of Object.entries(summedByDate)) {
        summedData.push({ event_date: key, [this.dataKey.snake]: value });
      }

      return summedData;
    },
    orderedChartData() {
      const orderedData = [...this.summedChartData].sort((a, b) => {
        const dateArrayA = a.event_date.split('-');
        const dateArrayB = b.event_date.split('-');
        const comparison = dateArrayA[0] - dateArrayB[0]
          || dateArrayA[1] - dateArrayB[1]
          || dateArrayA[2] - dateArrayB[2];

        return comparison;
      });

      return orderedData;
    },
    chartLineHeight() {
      return this.chartHeight -
        this.chartDefaults.xLabelPadding -
        this.chartDefaults.margin.top -
        this.chartDefaults.fontSize;
    },
    timeParsedData() {
      const parseDate = timeParse('%Y-%m-%d');

      return this.orderedChartData.map(d => {
        const obj = { ...d };

        obj[this.dimensionKey] = parseDate(d[this.dimensionKey]);

        return obj;
      });
    },
  },
  methods: {
    setTooltip(val) {
      this.tooltipPosition = val.position;
      this.hoveredIndex = val.hoveredIndex;

      this.updateTooltipContent(val.data);

      this.showTooltip = true;
    },
    updateTooltipContent(data) {
      const title = this.getTooltipTitle(data);
      const subtitle = this.getTooltipSubtitle();
      const content = this.getTooltipContent(data);

      this.tooltipContent.title = title;
      this.tooltipContent.subtitle = subtitle;
      this.tooltipContent.content = content;
    },
    getTooltipTitle(val) {
      return timeFormat('%b %d, %Y')(val.d[this.dimensionKey]);
    },
    getTooltipSubtitle() {
      return getReportFieldKeyFromSnake(this.allReportFields, this.dimensionKey);
    },
    getTooltipContent(val) {
      const content = [];
      const metricName = this.dataKey.key;
      const metricValue = getDisplayValue(this.dataKey, val.d[this.dataKey.snake]);

      if (val.metric) {
        content.push(getValueAndLabel(metricValue, this.dataKey));
      } else {
        content.push(`${metricName}: ${metricValue}`);
      }

      return content;
    },
    hideTooltip() {
      this.hoveredIndex = null;
      this.showTooltip = false;
    },
  },
};
</script>

<style lang="scss" scoped>

.line-chart {
  width: 100%;
}
</style>
